import { PayChannel, PayStatus } from './purchase';

export interface OrderPlaceInfo {
  goodsSpuVersion: GoodsSpuVersion;
  effectiveSpu: {
    spuCode: string;
    effectiveStatus: boolean;
    expireTime: string;
  };
}

export interface OrderPageParams {
  curPage?: number;
  size?: number;
  code?: string;
  unFinished?: boolean;
  payTypes?: PayChannel[];
}

export interface OrderPageResult {
  curPage: number;
  size: number;
  totalPage: number;
  totalNum: number;
  data: OrderClientPageResp[];
}

export interface OrderClientPageResp {
  /**
   * 订单CODE
   */
  code?: string;
  /**
   * 内容
   */
  contents?: ContentResp[];
  /**
   * 创建时间
   */
  createdAt?: number;
  /**
   * 创建人ID
   */
  createdBy?: string;
  /**
   * 订单ID
   */
  id?: string;
  /**
   * 组织ID
   */
  orgId?: string;
  /**
   * 支付方式
   */
  payType?: PayChannel;
  /**
   * 交易流水号
   */
  serialNo?: string;
  /**
   * 订单状态
   */
  status?: OrderStatus;
  /**
   * 合计实付金额
   */
  totalActualAmount?: number;
  /**
   * 合计优惠金额
   */
  totalDiscountedAmount?: number;
  /**
   * 合集应付金额
   */
  totalPayableAmount?: number;
  /**
   * 校验码 对公转账
   */
  checkCode?: string;
}

export interface ContentResp {
  /**
   * 内容
   */
  items?: ClientItemResp[];
  /**
   * 名称
   */
  name?: string;
  /**
   * 价格：单位分
   */
  price?: number;
  /**
   * 规格
   */
  specs?: {
    code: string; // eg: points_package
    option: string; // eg: point_count_03
    name: string; // eg: 点数包
    value: string; // eg: 基础包
  }[];
  saleBaseNum?: number;
}

/**
 * 订单状态
 */
export enum OrderStatus {
  Canceled = 'CANCELED',
  ConfirmReject = 'CONFIRM_REJECT',
  Confirmed = 'CONFIRMED',
  Created = 'CREATED',
  Finished = 'FINISHED',
  PayFail = 'PAY_FAIL',
  PaySuccessful = 'PAY_SUCCESSFUL',
  WaitingConfirm = 'WAITING_CONFIRM',
  WaitingPayment = 'WAITING_PAYMENT',
}

export interface ClientItemResp {
  /**
   * 编码
   */
  code?: string;
  /**
   * 名称
   */
  name?: string;
  /**
   * 值
   */
  value?: string;
}

export interface GoodsSpuVersion {
  id: string;
  code: string;
  spuSpecs: SpuSpecsItem[];
  spuItems: SpuItem[];
  skus: SkuItem[];
}

export interface SpuSpecsItem {
  code: string;
  name: string;
  options: {
    code: string;
    name: string;
  }[];
}

export interface SpuItem {
  id: string;
  code: string;
  name: string;
  desc: string;
  valueType: string;
  costType: string;
}

export interface SpecsItem {
  code: string;
  option: string;
}

export interface GoodsSkuVasItem {
  vasSpuCode: string;
  vasSpuName: string;
  giftNum: number;
  validDays: number;
  promotionType: string;
}

export interface SkuItem {
  id: string;
  specs: SpecsItem[];
  price: number;
  saleBaseNum: number;
  goodsSkuVasList: GoodsSkuVasItem[];
}
export interface OrderPayItem {
  payType: PayChannel; // 支付类型
  totalOriginalAmount: number; // 合计原价
  totalConvertedAmount?: number; // 合计折算金额
  totalPayableAmount: number; // 合计应付金额
  goods: {
    skuId: string;
    specs: SpecsItem[];
    spuVersionId: string; // 商品版本 id
    originalAmount: number; // 原价
    payableAmount: number; // 应付金额
    convertedAmount?: number; // 折算金额
    items: {
      code?: string;
      option?: string;
      value?: number | string;
    }[];
    skuVasList?: GoodsSkuVasItem[];
  };
}

export interface BatchOrderPayItem {
  payType: PayChannel; // 支付类型
  totalOriginalAmount: number; // 合计原价
  totalPayableAmount: number; // 合计应付金额
  goods: {
    skuId: string;
    specs: SpecsItem[];
    spuVersionId: string; // 商品版本 id
    originalAmount: number; // 原价
    payableAmount: number; // 应付金额
    items: {
      code?: string;
      option?: string;
      value?: number | string;
    }[];
  }[];
}

export const PackageCodeArray = [
  'point_count_03',
  'point_count_04',
  'point_count_05',
  'point_count_06',
] as const;

export type PackageCode = (typeof PackageCodeArray)[number];

export interface OrderListParams {
  code?: string; // 订单ID
  status?: string; // 订单状态
  unFinished?: boolean; // 是否未完成
  paymentTypes?: PayChannel[]; // 支付方式
  paymentStatus?: PayStatus[]; // 支付状态
}

export type PointWarningDialogType = 'three-day-remaining' | 'one-day-remaining' | 'insufficient';

export type PackageExpirationDialogType = 'three-day-expiring' | 'one-day-expiring' | 'expired';

export interface CafeBalanceInfo {
  // 套餐包
  lgPlanPackage?: PlanPackageInfo;
  // 企业号额度
  enterpriseQuota?: ExpansionQuotaInfo;
  // 员工号额度
  employeeQuota?: ExpansionQuotaInfo;
  // 客服坐席
  customerServiceAccount?: ExpansionQuotaInfo;
  // 后台自动化规则
  automationRule?: ExpansionQuotaInfo;
}

export interface ExpansionQuotaInfo {
  spuCode: string;
  spuName: string;
  totalPurchased?: number;
  totalUsed?: number;
}

/**
 * spec_code_100 免费版
 * spec_code_300 入门版
 * spec_code_400 专业版
 * spec_code_500 旗舰版
 */
export type PlanPackageVersionCode =
  | 'spec_code_100'
  | 'spec_code_300'
  | 'spec_code_400'
  | 'spec_code_500';

export interface PlanPackageInfo {
  spuCode: string;
  spuName: string;
  versionCode: PlanPackageVersionCode;
  version: string;
  // 接入账号数限额（总量）
  channelConnectAccountTotalPurchased: number;
  // 接入账号数（使用量）
  channelConnectAccountTotalUsed: number;
  // 客服坐席数限额（总量）
  customerServiceAccountTotalPurchased: number;
  // 客服坐席数（使用量）
  customerServiceAccountTotalUsed: number;
  // 后台自动化规则数（总量）
  automationRuleTotalPurchased: number;
  // 后台自动化规则数（使用量）
  automationRuleTotalUsed: number;
  // 生效时间
  activeTime: string;
  // 到期时间
  expireTime: string;
}

// 'spec_price_code_01', '月付';
// 'spec_price_code_02', '年付';
// 'spec_price_code_03', '日付';

export const ExpansionPackageArray = [
  'automation_rule_v2',
  'enterprise_quota_v2',
  'employee_quota_v2',
  'customer_service_account_v2',
] as const;

/**
 * 点数包名称映射
 */
export const PointPackageNameMap = {
  point_count_02: '体验包',
  point_count_03: '基础包',
  point_count_04: '标准包',
  point_count_05: '高级包',
  point_count_06: '尊享包',
} as const;
